<script lang="ts" setup>
import type {Ref} from "vue";
import {onMounted, onUnmounted, ref, watch} from "vue";

import {useFocusTrap} from "@vueuse/integrations/useFocusTrap";

import SlideTransition from "@src/components/ui/transitions/SlideTransition.vue";

const props = defineProps<{
    open: boolean;
    closeModal: () => void;
}>();

const modal: Ref<HTMLElement | undefined> = ref();

const {activate, deactivate} = useFocusTrap(modal);

// (event) close modal when clicking outside
const closeOnClick = (event: Event) => {
    if ((event.target as HTMLDivElement).id === "close-modal") {
        props.closeModal();
    }
};

// (event) close modal when typing esc button
const handleCloseOnEscape = (event: KeyboardEvent) => {
    if (["Escape", "Esc"].includes(event.key)) {
        props.closeModal();
    }
};

// set the handleCloseOnEscape when mounting the component
onMounted(() => {
    document.addEventListener("keydown", handleCloseOnEscape);
});

onUnmounted(() => {
    document.removeEventListener("keydown", handleCloseOnEscape);
    // remove the event when un-mounting the component
});

// toggle focus when the modal opens
watch(
    () => props.open,
    () => {
        if (props.open) {
            setTimeout(() => {
                activate();
            }, 500);
        } else {
            setTimeout(() => {
                deactivate();
            }, 200);
        }
    },
);
</script>

<template>
    <div
        v-if="props.open"
        :aria-hidden="!props.open"
        aria-labelledby="modal-title"
        aria-modal="true"
        class="fixed z-50 inset-0 overflow-y-auto"
        role="dialog"
    >
        <div
            v-if="props.open"
            class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0 backdrop-blur-[2px]">
            <div class="fixed inset-0 opacity-25 bg-gray-500 dark:bg-gray-400 transition-opacity"
                 @click="closeOnClick">
            </div>
            <div
                class="inline-block z-20 bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full md:max-w-3xl">

                <!--overlay-->
                <Transition name="fade">
                    <div
                        v-if="props.open"
                        class=""
                    ></div>
                </Transition>

                <!--modal-->
                <SlideTransition animation="slide-down">
                    <div
                        v-if="props.open"
                        class="fixed inset-0 z-10 h-full overflow-y-auto"
                    >
                        <div
                            id="close-modal"
                            ref="modal"
                            class="h-full flex items-center justify-center p-4 text-center sm:items-center sm:p-0"
                            @click="closeOnClick"
                        >
                            <!--content-->
                            <slot name="content"></slot>
                        </div>
                    </div>
                </SlideTransition>
            </div>
        </div>
    </div>
</template>

<style scoped>
.fade-enter-active {
    transition: opacity 0.1s ease;
}

.fade-leave-active {
    transition: opacity 0.6s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
